import { graphql, DataProps } from '@apollo/client/react/hoc';
import React from 'react';
import invariant from 'invariant';

import CurrentUser from 'auth/CurrentUser';
import FEATURE_QUERY from 'components/queries/FetchFeaturesQuery.gql';
import { FetchFeature, FetchFeatureVariables } from 'components/queries/__generated__/FetchFeature';

type WrapperProps = {
  name: string;
  on?: React.ReactElement<any>;
  off?: React.ReactElement<any>;
  children?: React.ReactElement<any>;
  injector?: (feature: string) => any;
};

type Props = {
  loading: boolean;
  hasFeature: boolean;
};

export const withFeature = function <P>(feature: string, propName?: string) {
  return graphql<P, FetchFeature, FetchFeatureVariables, any>(FEATURE_QUERY, {
    options: () => ({ variables: { feature } }),
    props: ({ data }) => ({
      loading: data && data.loading,
      [propName || 'hasFeature']: data && data.user ? data && !!data.user.feature : undefined,
    }),
  });
};

/* Render component based on the current user feature flag
 * Only a success handler is required: either the "on" prop or passing children.
 * null is rendered when the feature flag in turned off and "off" props is undefined.
 *
 * <Feature name="some_cool_feature" on={<SomeCoolComponent />} off={<SomeOldComponent />} />
 *
 * <Feature name="some_cool_feature">
 *   <SomeCoolComponent />
 * </Feature>
 */
function Feature(wrapperProps: WrapperProps): React.ReactElement<any> | null {
  const { name, on, off, injector, children } = wrapperProps;

  invariant(name, 'Feature - cannot render without a feature name');
  invariant(
    !on || !children,
    'Feature - what did you do? No both! I said "or" as in xor, you robot.'
  );

  const onCase = on || children || null;
  const offCase = off || null;

  if (!CurrentUser.isSignedIn() || CurrentUser.isLimitedUserAuth()) {
    // anon user can't have features
    return offCase;
  }

  const FeatureComponent = (injector || withFeature)(name)(function (props: Props) {
    const { hasFeature, loading } = props;

    if (loading || hasFeature === undefined) {
      return null;
    }

    return hasFeature ? onCase : offCase;
  });

  return <FeatureComponent />;
}

type FeatureSwitchProps = {
  name: string;
  children?: JSX.Element | null;
  on?: JSX.Element | null;
  off?: JSX.Element | null;
};

type ApolloProps = DataProps<FetchFeature, FetchFeatureVariables>;

const SwitchComponent = ({
  children = null,
  on = null,
  off = null,
  data,
}: FeatureSwitchProps & ApolloProps) => {
  if (data.loading) {
    return null;
  }

  if (data.user?.feature) {
    if (children && on) {
      console.warn('FeatureSwitch - children component has higher precedence over "on" component');
    }
    return children || on;
  }

  return off;
};

const FeatureSwitch = graphql<FeatureSwitchProps, FetchFeature, FetchFeatureVariables, ApolloProps>(
  FEATURE_QUERY,
  {
    options: (props) => ({
      variables: {
        feature: props.name,
      },
    }),
  }
)(SwitchComponent);

export { Feature, FeatureSwitch };
export default Feature;
